import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePanelist, usePublicPanel, useRequestCashout } from "../api";
import { ApiError } from "../api/errors";
import { ErrorState, SkeletonBlock } from "../components/ui/AsyncState";
import { Card } from "../components/ui/Card";
import { MfaNote } from "../components/ui/MfaNote";
import { Page } from "../components/ui/Page";
import { PaymentMethodOption } from "../components/ui/PaymentMethodOption";
import { usePanelPath } from "../routing";
import type { CashoutMethodId } from "../types";
import { formatNumber, walletState } from "../utils";

const methods = [
  {
    id: "paypal",
    icon: "🅿",
    subtitle: "Sent to your PayPal email within 5 working days",
    title: "PayPal",
  },
  {
    id: "bank",
    icon: "🏦",
    subtitle: "UK bank account, 5 working days",
    title: "Bank transfer",
  },
  {
    id: "voucher",
    icon: "🎁",
    subtitle: "Amazon, M&S, Argos and more",
    title: "Gift voucher",
  },
] satisfies Array<{
  id: CashoutMethodId;
  icon: string;
  subtitle: string;
  title: string;
}>;

export function CashoutPage() {
  const panelPath = usePanelPath();
  const panelQuery = usePublicPanel();
  const { data: panelist, error, isLoading } = usePanelist();
  const requestCashout = useRequestCashout();
  const cashoutKeyRef = useRef<string | undefined>(undefined);
  const [selectedMethod, setSelectedMethod] =
    useState<CashoutMethodId>("paypal");
  const wallet = walletState(panelQuery.data, panelist?.points ?? 0);

  if (isLoading) {
    return (
      <Page>
        <div className="cashout-wrap">
          <SkeletonBlock count={4} height={54} />
        </div>
      </Page>
    );
  }

  if (error || !panelist) {
    return (
      <Page>
        <ErrorState error={error} title="Could not load cashout details" />
      </Page>
    );
  }

  const loadedPanelist = panelist;

  function submitCashout() {
    cashoutKeyRef.current ??= crypto.randomUUID();

    requestCashout.mutate(
      {
        // amountPts: loadedPanelist.points,
        idempotencyKey: cashoutKeyRef.current,
        method: selectedMethod,
      },
      {
        onError: (error) => {
          if (
            error instanceof ApiError &&
            (error.status === 400 || error.status === 422)
          ) {
            cashoutKeyRef.current = undefined;
          }
        },
        onSuccess: () => {
          cashoutKeyRef.current = undefined;
        },
      },
    );
  }

  return (
    <Page>
      <div className="cashout-wrap">
        <Link className="back-link" to={panelPath("/")}>
          ← Back to dashboard
        </Link>

        {!wallet.canCashout && (
          <div className="alert alert-warn">
            <strong>Minimum 500 points required</strong>
            <span>
              You have {loadedPanelist.points} points (≈{" "}
              {loadedPanelist.pointsGbp}). Complete more tasks to unlock
              cashout.
            </span>
          </div>
        )}

        <Card style={{ marginBottom: 18 }} title="Your balance">
          <div
            style={{
              alignItems: "baseline",
              display: "flex",
              gap: 12,
              marginBottom: 8,
            }}
          >
            <span
              style={{
                color: "var(--primary)",
                fontFamily: "var(--font-h)",
                fontSize: 34,
                fontWeight: 600,
              }}
            >
              {formatNumber(loadedPanelist.points)} pts
            </span>
            <span style={{ color: "var(--sand)", fontSize: 15 }}>
              ≈ {loadedPanelist.pointsGbp}
            </span>
          </div>
          <div
            style={{
              background: "var(--cream-dk)",
              borderRadius: 3,
              height: 5,
              marginBottom: 6,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "var(--accent)",
                borderRadius: 3,
                height: 5,
                width: `${wallet.pct}%`,
              }}
            />
          </div>
          <div style={{ color: "var(--sand)", fontSize: 12 }}>
            {wallet.toNext} pts to go (minimum 500 pts = £10)
          </div>
        </Card>

        <p
          style={{
            color: "var(--text)",
            fontSize: 13.5,
            fontWeight: 500,
            marginBottom: 12,
          }}
        >
          Choose payment method
        </p>

        {methods.map((method) => (
          <PaymentMethodOption
            icon={method.icon}
            id={method.id}
            key={method.id}
            onSelect={setSelectedMethod}
            selected={selectedMethod === method.id}
            subtitle={method.subtitle}
            title={method.title}
          />
        ))}

        <MfaNote hidden={selectedMethod !== "bank"}>
          Providing bank details will trigger an identity verification (MFA)
          step
        </MfaNote>

        {requestCashout.error && (
          <ErrorState
            error={requestCashout.error}
            title="Could not request cashout"
          />
        )}
        {requestCashout.isSuccess && (
          <div className="alert alert-success">
            <strong>Cashout requested</strong>
            <span>Your request has been queued for manual processing.</span>
          </div>
        )}
        <button
          className="btn btn-accent btn-full"
          disabled={!wallet.canCashout || requestCashout.isPending}
          onClick={submitCashout}
          style={{ marginTop: 18 }}
          type="button"
        ></button>
        <p
          style={{
            color: "var(--sand)",
            fontSize: 11.5,
            marginTop: 8,
            textAlign: "center",
          }}
        >
          {wallet.canCashout ? "" : "Minimum 500 points (≈ £10) required"}
        </p>
      </div>
    </Page>
  );
}
